import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { useActorRef } from '@xstate/react';

import { modalMachine } from './modal-machine';

import type { ModalMachineEvents } from './modal-machine.types';

export const useModalMachine = (props: UseModalMachineProps) => {
  const { initial = 'dismissed', onShowing, onShown, onDismissing, onDismissed } = props;

  // ──────────────────────────────────────────────────────────────────────────────

  const machine = useMemo(
    () => modalMachine.provide({ guards: { checkIfInitiallyShown: () => initial === 'shown' } }),
    [initial],
  );

  const eventHandlersMapping = useMemo<ModalEventHandlersMapping>(
    () => ({
      START_SHOW: onShowing,
      SHOW: onShown,
      START_DISMISS: onDismissing,
      DISMISS: onDismissed,
    }),
    [onDismissed, onDismissing, onShowing, onShown],
  );

  const eventHandlersMappingRef = useRef(eventHandlersMapping);

  useLayoutEffect(() => {
    eventHandlersMappingRef.current = eventHandlersMapping;
  }, [eventHandlersMapping]);

  const modalService = useActorRef(machine);

  //
  // ─── EVENT DISPATCHERS ────────────────────────────────────────────────────────
  //

  const sendModalEvent = useCallback(
    (eventType: ModalMachineEvents['type']) => {
      const previousSnapshot = modalService.getSnapshot();

      modalService.send({ type: eventType });

      const hasChanged = !modalService.getSnapshot().matches(previousSnapshot.value);

      if (!hasChanged) return;

      eventHandlersMappingRef.current[eventType]?.();
    },
    [modalService],
  );

  const sendStartShowEvent = useCallback(() => {
    sendModalEvent('START_SHOW');
  }, [sendModalEvent]);

  const sendShowEvent = useCallback(() => {
    sendModalEvent('SHOW');
  }, [sendModalEvent]);

  const sendDismissEvent = useCallback(() => {
    sendModalEvent('DISMISS');
  }, [sendModalEvent]);

  const sendStartDismissEvent = useCallback(() => {
    sendModalEvent('START_DISMISS');
  }, [sendModalEvent]);

  // ──────────────────────────────────────────────────────────────────────────────

  return useMemo(
    () => ({
      modalService,
      helpers: {
        sendStartShowEvent,
        sendShowEvent,
        sendDismissEvent,
        sendStartDismissEvent,
      },
    }),
    [modalService, sendDismissEvent, sendShowEvent, sendStartDismissEvent, sendStartShowEvent],
  );
};

//
// ─── TYPES ──────────────────────────────────────────────────────────────────────
//

type UseModalMachineProps = Readonly<{
  initial: 'shown' | 'dismissed';
  onShowing?: ModalEventHandler;
  onShown?: ModalEventHandler;
  onDismissing?: ModalEventHandler;
  onDismissed?: ModalEventHandler;
}>;

type ModalEventHandler = () => void;

type ModalEventHandlersMapping = Record<ModalMachineEvents['type'], ModalEventHandler | undefined>;
